import { useCallback, useEffect, useState } from 'react';
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation,
  Link,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { handleSocketsEventsByType } from 'redux/sockets';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Drawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddEditSchedule from 'pages/Schedule/addEditSchedule';
import {
  getBusinessConfig,
  getProviders,
  getProvidersStatuses,
} from 'redux/actions/providers';
import { updateStopResumeOrders } from 'redux/actions/providers';
import Schedule from 'pages/Schedule/Schedule';
import Providers from 'pages/Providers/index';
import Settings from 'pages/Settings';
import WebsiteSettings from 'pages/WebsiteSettings';
import Orders from 'pages/Orders';
import Help from 'pages/Help';
import Menu from 'pages/MenuNew/index';
import InventoryItems from 'pages/Inventory/Items';
import InventoryItemForm from 'pages/Inventory/ItemForm';
import InventoryCategories from 'pages/Inventory/Categories';
import CategoryEdit from 'pages/Inventory/CategoryEdit';
import InventoryManagement from 'pages/InventoryManagement';
import ProfitIQ from 'pages/ProfitIQ';
import Reports from 'pages/Report';
import PosSettings from 'pages/PosSettings';
import PosMenuIngredients from 'pages/PosMenu/Ingredients';
import PosMenuItems from 'pages/PosMenu/Items';
import PosMenuItemEdit from 'pages/PosMenu/Items/ItemEdit';
import PosMenuModifierGroups from 'pages/PosMenu/ModifierGroups';
import PosMenuModifierGroupEdit from 'pages/PosMenu/ModifierGroups/ModifierGroupEdit';
import PosMenuOverview from 'pages/PosMenu/Overview';
import PosMenuPreview from 'pages/PosMenu/Preview';
import PosMenuImport from 'pages/PosMenu/ImportMenu';
import PosMenuAiImport from 'pages/PosMenu/AiImportMenu';
import Sidebar from 'components/Sidebar';
import Preloader from 'components/Preloader';
import ProtectedRoute from 'components/ProtectedRoute';
import { useSocket } from 'providers/Socket';
import BrandSignUp from 'pages/Auth/BrandSignUp/index';
import ResumePause from 'components/PauseResume/index';
import OverlayOrders from 'components/Order/OverlayOrders';
import Header from 'components/Header/index';
import { useIsWidthDown } from 'hooks/mediaQuery';
import {
  RESET_MODIFIED_ORDERS,
  ORDERS_EMPTY,
  RESET_CANCELED_ORDERS,
} from 'redux/actions/types';
import OrdersHistory from 'pages/OrderHistory';
import AddLocation from 'pages/AddLocation';
import PaymentNotificationDialog from 'components/PaymentNotificationModal';
import { getPaymentInfo } from 'redux/actions/paymentNotification';
import InvitationLocation from 'pages/InvitationLocation/InvitationLocation';
import { isPermissionWithCompanyType } from 'hooks/useCompanyType';
import * as all from 'constants/permissions';
import usePermission from 'hooks/permission';
import { isSuperRole } from 'utils/roles';
import usePermissionPlan from 'hooks/permissionPlan';
import TableOrdering from 'pages/TableOrdering';
import { SkeletonWrap } from 'components/Settings/Skeleton/SkeletonSidebarList';
import { companyTypesEnum } from 'constants/companyTypes';
import OnBoarding from 'components/OnBoarding/OnBoarding';
import ChargeBack from 'pages/Chargeback';
import NewHome from 'components/NewHome';
import useQuery from 'hooks/useQuery';
import HubSpotChatPage from 'pages/HubSpotChatPage';
import ResumePauseProviderIndex from 'components/PauseResume/PauseResumeProviderIndex';
import UpdatePlanWix from 'components/Wix/UpdatePlanWix';
import useWixFreePlan from 'hooks/useWixFreePlan';
import { INITIAL_PERIOD } from 'constants/config';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import {
  getBrandList,
  getBusinessClassification,
} from 'redux/actions/business';
import Private from 'components/Private';
import { useMenuAmDomainContext } from 'context/menuAmDomainContext';
import MainLogo from 'assets/images/logo.png';
import ReportGenerator from './Reporting/ReportGenerator';
import PosReportsPage from './Reporting/PosReportsPage';
import PromotionsPage from 'pages/Promotions';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';
import DeliveryService from './WebsiteSettings/DeliveryService';
import DeliveryReportsPage from './Reporting/DeliveryReportsPage';
import TopSellerItemsPage from './Reporting/TopSellerItemsPage';
import SalesByCategoryPage from './Reporting/SalesByCategoryPage';
import InventoryDamagePage from './Reporting/InventoryDamagePage';
import PromotionReportPage from './Reporting/PromotionReportPage';
import {
  SCREEN_NOTIFICATION_MODES,
  getScreenNotificationMode,
  setScreenNotificationMode,
  subscribeScreenNotificationMode,
} from 'utils/screenNotification';
// import { getBrandList } from 'redux/actions/business';

const drawerWidth = 240;
const ORDER_NOTIFICATION_TOAST_ID = 'order-notification-toast';

const pluralizeOrderNotification = (label, count) =>
  `${count} ${label}${count > 1 ? 's' : ''}`;

// Styled Components
const MainBlock = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  overflowY: 'auto',
  flexGrow: 1,
  width: '100%',
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

function Main() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { t } = useTranslation();
  const checkPermission = usePermission();
  const checkPermissionPlan = usePermissionPlan();

  const { isTypeSingle } = isPermissionWithCompanyType();
  const mdDown = useIsWidthDown('md');
  const { subscribe, unsubscribe, eventEmit } = useSocket();
  const [overlay, setOverlay] = useState(false);
  const [screenNotificationMode, setScreenNotificationModeState] = useState(
    getScreenNotificationMode
  );
  const [joined, setJoined] = useState(false);
  const query = useQuery();
  const mode = query.get('mode');
  const entityType = query.get('entityType');
  const { logo, isMenuamDomain, name: menuamName } = useMenuAmDomainContext();

  const {
    data,
    newOrders,
    modifiedOrders,
    lastUpdatedId,
    lastCanceledId,
    canceledOrders,
    providers,
  } = useSelector(
    ({
      user: { data, loading },
      orders: {
        newOrders,
        modifiedOrders,
        lastUpdatedId,
        lastCanceledId,
        canceledOrders,
      },
      providers,
    }) => ({
      data,
      loading,
      newOrders,
      modifiedOrders,
      canceledOrders,
      lastUpdatedId,
      lastCanceledId,
      providers,
    })
  );
  const { wixFreePlan } = useWixFreePlan();
  const { currentBusiness } = useSelector(({ business }) => business);
  const { items, stopResumeOrders } = useSelector(({ providers }) => providers);
  const promotionsEnabled = usePromotionsEnabled();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const canViewOrders = checkPermission(all['ORDER_VIEW']);
  const providerCheck =
    window.location.pathname.includes('/providers') &&
    checkPermissionPlan(all['APP_BUNDLE']) &&
    checkPermission(all['PROVIDERS_VIEW']) &&
    isTypeSingle
      ? !providers?.items?.length
      : 0;
  const skeletonLoading = !currentBusiness?.roles || !data || providerCheck;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (mode === 'create' && entityType === 'modifier') {
      window.scrollTo(0, 0);
    }
  }, [mode, entityType]);

  useEffect(() => {
    setOpen(!mdDown);
  }, [mdDown]);

  useEffect(() => {
    // if (checkPermission(all['PROVIDERS_VIEW'])) {
    dispatch(getProviders());
    // }
    if (currentBusiness?.type === companyTypesEnum.SINGLE) {
      dispatch(getProvidersStatuses());
    }
  }, [currentBusiness?._id]);

  useEffect(() => {
    if (
      currentBusiness?._id &&
      currentBusiness?.roles?.name &&
      !isSuperRole(currentBusiness?.roles)
    ) {
      dispatch(getPaymentInfo(currentBusiness._id));
    }
  }, [currentBusiness]);

  useEffect(() => {
    const handleSocketEvent = (data) => {
      handleSocketsEventsByType(data);
    };
    subscribe('event-bus', handleSocketEvent);

    return () => {
      unsubscribe('event-bus', handleSocketEvent);
    };
  }, [subscribe, unsubscribe]);

  useEffect(() => {
    dispatch(getBusinessClassification());
  }, []);

  useEffect(() => {
    // Make the getBrandList request asynchronous to avoid blocking the UI
    const fetchBrandList = async () => {
      try {
        if (
          location.pathname === '/history' ||
          location.pathname === '/reports'
        ) {
          dispatch(getBrandList());
        }
      } catch (error) {
        console.error('Failed to fetch brand list:', error);
      }
    };

    // Use setTimeout to defer the execution to the next event loop
    // This ensures the UI renders first before making the API call
    const timeoutId = setTimeout(() => {
      fetchBrandList();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  useEffect(() => {
    if (data) {
      setJoined(true);
      if (!joined) {
        eventEmit('join', { id: data._id });
      }
    }
  }, [data, joined, eventEmit]);

  useEffect(() => {
    const unsubscribe = subscribeScreenNotificationMode((mode) => {
      setScreenNotificationModeState(mode);
    });

    return unsubscribe;
  }, []);

  const getOrderNotificationToastMessage = useCallback(() => {
    return [
      canceledOrders.length
        ? pluralizeOrderNotification(
            t('orders.canceled_order'),
            canceledOrders.length
          )
        : '',
      modifiedOrders.length
        ? pluralizeOrderNotification(
            t('orders.updated_order'),
            modifiedOrders.length
          )
        : '',
      newOrders.length
        ? pluralizeOrderNotification(t('orders.new_order'), newOrders.length)
        : '',
    ]
      .filter(Boolean)
      .join('\n');
  }, [canceledOrders.length, modifiedOrders.length, newOrders.length, t]);

  const showOrderNotificationToast = useCallback(() => {
    const message = getOrderNotificationToastMessage();
    const toastOptions = {
      toastId: ORDER_NOTIFICATION_TOAST_ID,
      autoClose: false,
      closeOnClick: true,
      onClick: () => {
        setOverlay(true);
        toast.dismiss(ORDER_NOTIFICATION_TOAST_ID);
      },
    };

    if (toast.isActive(ORDER_NOTIFICATION_TOAST_ID)) {
      toast.update(ORDER_NOTIFICATION_TOAST_ID, {
        render: message,
        ...toastOptions,
      });
      return;
    }

    toast(message, toastOptions);
  }, [getOrderNotificationToastMessage]);

  useEffect(() => {
    const hasOrderNotifications =
      newOrders.length || modifiedOrders.length || canceledOrders.length;

    if (
      !hasOrderNotifications ||
      !canViewOrders ||
      screenNotificationMode === SCREEN_NOTIFICATION_MODES.OFF
    ) {
      setOverlay(false);
      toast.dismiss(ORDER_NOTIFICATION_TOAST_ID);
      return;
    }

    if (screenNotificationMode === SCREEN_NOTIFICATION_MODES.BANNER) {
      setOverlay(false);
      showOrderNotificationToast();
      return;
    }

    toast.dismiss(ORDER_NOTIFICATION_TOAST_ID);
    setOverlay(true);
  }, [
    canViewOrders,
    newOrders.length,
    modifiedOrders.length,
    canceledOrders.length,
    screenNotificationMode,
    showOrderNotificationToast,
  ]);

  // Removed useLayoutEffect for dynamic margin calculation

  useEffect(() => {
    dispatch(getBusinessConfig());
  }, []);

  const openOrders = () => {
    const { period } = qs.parse(search, { ignoreQueryPrefix: true });
    const periodValue = period ? period : INITIAL_PERIOD;
    history.push({
      pathname: `/orders${
        newOrders.length || modifiedOrders.length || canceledOrders.length
          ? `/${lastCanceledId || lastUpdatedId}`
          : ''
      }`,
      search: `?period=${periodValue}&filter=all`,
    });
    setOverlay(false);
    dispatch({ type: ORDERS_EMPTY });
    dispatch({ type: RESET_MODIFIED_ORDERS });
    dispatch({ type: RESET_CANCELED_ORDERS });
  };

  const handleShowOrderNotificationsAsToastChange = (enabled) => {
    const hasOrderNotifications =
      newOrders.length || modifiedOrders.length || canceledOrders.length;
    const nextMode = enabled
      ? SCREEN_NOTIFICATION_MODES.BANNER
      : SCREEN_NOTIFICATION_MODES.WHOLE_SCREEN;

    setScreenNotificationModeState(nextMode);
    setScreenNotificationMode(nextMode);

    if (!hasOrderNotifications) {
      return;
    }

    if (enabled) {
      setOverlay(false);
      showOrderNotificationToast();
      return;
    }

    toast.dismiss(ORDER_NOTIFICATION_TOAST_ID);
    setOverlay(true);
  };

  if (location.pathname.includes('/addBrand')) {
    return <BrandSignUp data={data} />;
  }
  if (location.pathname.includes('/addLocation')) {
    return <AddLocation data={data} />;
  }
  if (location.pathname.includes('/invitationLocation')) {
    return <InvitationLocation />;
  }
  if (location.pathname.includes('/help/chat')) {
    return (
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Switch>
          <Route path="/help/chat/:businessId" component={HubSpotChatPage} />
        </Switch>
      </Box>
    );
  }

  const getBusinessTitle = (business) => {
    if (business) {
      const { title, companyName, disabled, locationInfo } = business;
      const name = title || companyName || '';
      const location = locationInfo?.locationName
        ? ` - ${locationInfo.locationName}`
        : '';
      const status = disabled ? ' (Disabled)' : '';
      return `${name}${location}${status}`;
    }
    return '';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '80vh',
        gap: '0px', // 30px gap between Header and Content
      }}
    >
      {/* Header */}
      <Header
        handleDrawerOpen={handleDrawerOpen}
        open={open}
        drawerWidth={drawerWidth}
        sx={{ boxSizing: 'border-box' }}
      />

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Drawer */}
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: '#3c4351',
            },
            '& .MuiListItemIcon-root': {
              color: (theme) => theme.palette.common.white,
            },
            '& .MuiListItemText-root': {
              color: (theme) => theme.palette.common.white,
            },
            '& .MuiButtonBase-root': {
              color: (theme) => theme.palette.common.white,
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <Box
            id="drawerHeader"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              boxSizing: 'border-box',
              height: '70px',
              padding: '8px',
              borderBottom: '1px solid #646973',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {skeletonLoading ? (
                <Box
                  sx={{
                    width: '24px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <SkeletonWrap variant="circular" width={24} height={24} />
                </Box>
              ) : (
                <IconButton
                  id="mainChevron"
                  disableRipple
                  onClick={handleDrawerClose}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ListItem to="/" component={Link}>
                  <ListItemIcon sx={{ width: '40px' }}>
                    {skeletonLoading ? (
                      <SkeletonWrap variant="circular" width={40} height={40} />
                    ) : (
                      <img
                        src={isMenuamDomain ? logo : MainLogo}
                        alt="orders.co"
                        style={{ height: '40px', width: '40px' }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    sx={{ color: (theme) => theme.palette.common.white }}
                    primary={
                      <Box>
                        {skeletonLoading ? (
                          <SkeletonWrap
                            animation="wave"
                            height={20}
                            width="64px"
                          />
                        ) : (
                          <Typography>
                            {isMenuamDomain ? `${menuamName}.com` : 'Orders.co'}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </Box>
            </Box>
          </Box>
          <Box sx={{ borderBottom: '1px solid #646973', height: '48px' }}>
            {skeletonLoading ? (
              <SkeletonWrap animation="wave" height={30} width="77px" />
            ) : (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#bcbec3',
                  px: 2,
                  py: 2,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden', // ✅ Corrected from 'ellipsis' to 'hidden'
                  lineHeight: '16px',
                }}
              >
                {getBusinessTitle(currentBusiness)}
              </Typography>
            )}
          </Box>
          <Sidebar />
        </Drawer>

        {/* MainBlock */}
        <MainBlock open={open}>
          <Box>
            {skeletonLoading || !providers.items ? (
              <Preloader overlay className="app-loader" />
            ) : (
              <Box>
                <Switch>
                  {data?.roles?.name !== 'user' && (
                    <ProtectedRoute
                      exact
                      permission="HOME_REPORT_FILTER"
                      path="/"
                      component={wixFreePlan ? UpdatePlanWix : NewHome}
                    />
                  )}
                  <Route
                    exact
                    path="/help"
                    component={wixFreePlan ? UpdatePlanWix : Help}
                  />
                  <ProtectedRoute
                    exact
                    permission="ORDER_VIEW"
                    path="/orders/:current?"
                    component={wixFreePlan ? UpdatePlanWix : Orders}
                  />
                  <Route
                    exact
                    path="/onboarding"
                    component={wixFreePlan ? UpdatePlanWix : OnBoarding}
                  />
                  <Route
                    exact
                    path="/history/:current?"
                    component={wixFreePlan ? UpdatePlanWix : OrdersHistory}
                  />
                  <Route
                    exact
                    path="/schedule"
                    component={wixFreePlan ? UpdatePlanWix : Schedule}
                  />
                  <ProtectedRoute
                    exact
                    path="/schedule/new/:id?"
                    component={wixFreePlan ? UpdatePlanWix : AddEditSchedule}
                  />
                  <Route
                    path="/settings"
                    component={wixFreePlan ? UpdatePlanWix : Settings}
                  />
                  <ProtectedRoute
                    path="/website"
                    component={wixFreePlan ? UpdatePlanWix : WebsiteSettings}
                  />
                  <ProtectedRoute
                    permission="SETTINGS_WEBSITE"
                    path="/delivery-service"
                    component={wixFreePlan ? UpdatePlanWix : DeliveryService}
                  />
                  <ProtectedRoute
                    permission="HOME_REPORT_FILTER"
                    path="/reports"
                    component={wixFreePlan ? UpdatePlanWix : Reports}
                  />
                  <Route
                    path="/menu"
                    component={wixFreePlan ? UpdatePlanWix : Menu}
                  />
                  <Route
                    exact
                    path="/pos-menu"
                    render={() => <Redirect to="/pos-menu/overview" />}
                  />
                  <Route
                    path="/pos-menu/overview"
                    component={PosMenuOverview}
                  />
                  <Route path="/pos-menu/preview" component={PosMenuPreview} />
                  <Route
                    exact
                    path="/pos-menu/items"
                    component={PosMenuItems}
                  />
                  {/* Keyed by id: moving between entities starts a fresh editor */}
                  <Route
                    path="/pos-menu/items/:id"
                    render={(props) => (
                      <PosMenuItemEdit key={props.match.params.id} {...props} />
                    )}
                  />
                  <Route
                    exact
                    path="/pos-menu/modifier-groups"
                    component={PosMenuModifierGroups}
                  />
                  <Route
                    path="/pos-menu/modifier-groups/:id"
                    render={(props) => (
                      <PosMenuModifierGroupEdit
                        key={props.match.params.id}
                        {...props}
                      />
                    )}
                  />
                  <Route
                    path="/pos-menu/ingredients"
                    component={PosMenuIngredients}
                  />
                  <Route path="/pos-menu/import" component={PosMenuImport} />
                  <Route
                    path="/pos-menu/ai-import"
                    component={PosMenuAiImport}
                  />
                  <Route
                    exact
                    path="/inventory"
                    render={() => <Redirect to="/inventory/items" />}
                  />
                  <Route
                    exact
                    path="/inventory/items"
                    component={InventoryItems}
                  />
                  <Route
                    exact
                    path="/inventory/items/new"
                    component={InventoryItemForm}
                  />
                  <Route
                    exact
                    path="/inventory/items/inventory/:inventoryUuid"
                    component={InventoryItemForm}
                  />
                  <Route
                    exact
                    path="/inventory/items/:id"
                    component={InventoryItemForm}
                  />
                  <Route
                    exact
                    path="/inventory/categories"
                    component={InventoryCategories}
                  />
                  <Route
                    exact
                    path="/inventory/categories/new"
                    component={CategoryEdit}
                  />
                  <Route
                    exact
                    path="/inventory/categories/:id"
                    component={CategoryEdit}
                  />
                  <ProtectedRoute
                    permission="MENU_EDIT"
                    path="/inventory-management"
                    component={
                      wixFreePlan ? UpdatePlanWix : InventoryManagement
                    }
                  />
                  <Route
                    path="/chargeback"
                    component={wixFreePlan ? UpdatePlanWix : ChargeBack}
                  />
                  <Route
                    path="/table-ordering"
                    component={wixFreePlan ? UpdatePlanWix : TableOrdering}
                  />
                  <Route
                    path="/table-setup"
                    component={wixFreePlan ? UpdatePlanWix : PosSettings}
                  />
                  <ProtectedRoute
                    path="/providers"
                    component={wixFreePlan ? UpdatePlanWix : Providers}
                    permission="PROVIDERS_VIEW"
                  />
                  <Route path="/report-generator" component={ReportGenerator} />
                  <Route
                    path="/pos-reports/summary"
                    component={PosReportsPage}
                  />
                  <Route
                    path="/pos-reports/delivery"
                    component={DeliveryReportsPage}
                  />
                  <Route
                    path="/pos-reports/top-sellers"
                    component={TopSellerItemsPage}
                  />
                  <Route
                    path="/pos-reports/sales-by-category"
                    component={SalesByCategoryPage}
                  />
                  <Route
                    path="/pos-reports/inventory-damage"
                    component={InventoryDamagePage}
                  />
                  <Route
                    path="/pos-reports/promotions"
                    component={PromotionReportPage}
                  />
                  {promotionsEnabled && (
                    <Route path="/promotions" component={PromotionsPage} />
                  )}
                  <ProtectedRoute
                    permission="PROFIT_IQ_VIEW"
                    path="/profit-iq"
                    component={wixFreePlan ? UpdatePlanWix : ProfitIQ}
                  />
                  <Redirect from="/payment-success" to="/website/main" />
                  {/* <Redirect
                    to={data?.roles?.name !== 'user' ? '/' : '/orders'}
                  /> */}
                </Switch>
              </Box>
            )}
          </Box>
          <PaymentNotificationDialog />
          {overlay ? (
            <Private permission="ORDER_VIEW">
              <OverlayOrders
                openOrders={openOrders}
                setOverlay={setOverlay}
                newOrders={newOrders}
                modifiedOrders={modifiedOrders}
                canceledOrders={canceledOrders}
                showAsToast={
                  screenNotificationMode === SCREEN_NOTIFICATION_MODES.BANNER
                }
                onShowAsToastChange={handleShowOrderNotificationsAsToastChange}
              />
            </Private>
          ) : (
            <></>
          )}
          {isTypeSingle && stopResumeOrders?.provider === 'all' ? (
            <ResumePause
              providers={items}
              updateStopResumeOrders={updateStopResumeOrders}
              stopResumeOrders={stopResumeOrders}
            />
          ) : (
            <>
              {isTypeSingle && stopResumeOrders?.provider !== 'all' ? (
                <ResumePauseProviderIndex
                  providers={items}
                  updateStopResumeOrders={updateStopResumeOrders}
                  stopResumeOrders={stopResumeOrders}
                />
              ) : (
                <></>
              )}
            </>
          )}
        </MainBlock>
      </Box>
    </Box>
  );
}

export default Main;
